import React, { memo } from "react";
// design-system: rn-primitive waiver — portal hosts need RN View's pointerEvents="box-none" (the web Box atom renders a div without it)
// eslint-disable-next-line no-restricted-imports -- RFC-#6 genuine-runtime: native portal-host overlay. Root Views use pointerEvents="box-none" + StyleSheet.absoluteFill geometry (position absolute,…
import { StyleSheet, View } from "react-native";

import HeaderPlayer from "@/components/organisms/HeaderPlayer";

import MetricExplanationModal from "@/components/organisms/MetricExplanationModal";
import MetricItem from "@/components/organisms/PlayerDetailCenter/MetricItem";
import TransferValueModal from "@/components/organisms/TransferValueModal";
import { ScrollView } from "@/components/atoms/ScrollView";
import { Stack } from "@/components/atoms/Stack";
import { PortalHost, PLAYER_DETAIL_PORTAL } from "@/components/atoms/Portal";

import AIPlayerSummary from "@/features/player-detail/components/organisms/AIPlayerSummary";
import AIPlayerSummaryErrorBoundary from "@/features/player-detail/components/molecules/AIPlayerSummaryErrorBoundary";
import PlayerPageHeader from "@/features/player-detail/components/organisms/PlayerPageHeader";
import {
  PlayerDetailListPlayersByTeamIdsAndPositionIdsQuery,
  PlayerDetailPlayerMetaFragment,
  Watchlist,
} from "@/generated/graphql";
import { ICompositeMetric } from "@/stores/compositeMetric";
import { ifWeb } from "@/utils/platform";

/**
 * Props for PlayerDetailCenterView component
 */
interface ICenterColumnProps {
  player: PlayerDetailPlayerMetaFragment;
  activeWatchList?: Watchlist;
  onAddPlayer?: () => void;
  isGoBack?: boolean;
  isSimilarDetail?: boolean;
  playerCompositeMetricsArray: (ICompositeMetric | null)[][];
  listPlayerSameTeamAndPosition:
    | PlayerDetailListPlayersByTeamIdsAndPositionIdsQuery
    | undefined;
  isFetchingListPlayerSameTeamAndPosition: boolean;
  numCols: number;
  isSmallScreen: boolean;
  isDark: boolean;
  explanationModalOpen: boolean;
  selectedMetricId: string | null;
  selectedMetricValue: number | string | null;
  selectedRosterValues: number[];
  handleMetricSelect: (compositeMetric: ICompositeMetric) => void;
  handleCloseModal: () => void;
  onNavigateToReports: () => void;
  transferValueModalOpen: boolean;
  handleTransferValueRangeClick: () => void;
  handleCloseTransferValueModal: () => void;
  currencyCode: import("@/generated/graphql").CurrencyCode;
  playerPageHeaderEnabled: boolean;
}

/**
 * Builds the scroll-content padding classes for the detail column.
 *
 * SE-6776 removed a trailing platform-forked pair of classes that BOTH emitted
 * nothing — the native branch named a 240px padding step the closed spacing
 * scale dropped at theme closure, and the web branch was a typo for an axis
 * Tailwind has never had. Since that removal the column has rendered with NO
 * bottom clearance at all.
 *
 * SE-6782 restores the author's original intent (SE-3252): 240px of scroll
 * clearance beneath the back button on native, so the last metric row is not
 * trapped under it. The value rides a tracked 240px spacing token (see the
 * tracked-debt block in tailwind.config.js) rather than snapping to the
 * ladder's 64px ceiling, which would have been a 27% approximation of a value
 * nobody has reviewed. This ADDS padding that is not currently rendered — it
 * is a real native visual change.
 *
 * The token name is deliberately not repeated in this prose: the tracking
 * prefix is a burn-down counter, and a prose mention would inflate it.
 * @param playerPageHeaderEnabled - Whether the PlayerPageHeader flag is on
 * @param isSimilarDetail - Whether rendering inside the similar-players view
 * @param isGoBack - Whether the back button is shown (it is what needs clearing)
 * @returns The className string for the ScrollView content container
 */
const getScrollContentClassName = (
  playerPageHeaderEnabled: boolean,
  isSimilarDetail: boolean | undefined,
  isGoBack: boolean | undefined
): string => {
  const webPadding = `px-4 ${playerPageHeaderEnabled ? "pt-2" : "pt-6"}`;
  const nativeTopPadding = isSimilarDetail ? undefined : "pt-0";
  const nativeBackClearance = isGoBack ? "pb-needs-semantic-240px" : undefined;
  const nativePadding =
    [nativeTopPadding, nativeBackClearance].filter(Boolean).join(" ") ||
    undefined;
  return `p-3 ${ifWeb(webPadding, nativePadding)}`;
};

/**
 * View component for player detail center column with composite metrics, AI summary, and modals
 * @param root0 - Component props
 * @param root0.player - Player data fragment containing all player information
 * @param root0.isGoBack - Whether to show back navigation button
 * @param root0.isSimilarDetail - Whether rendering in similar players comparison view
 * @param root0.onAddPlayer - Callback invoked when adding player to watchlist
 * @param root0.activeWatchList - Currently selected watchlist for player context
 * @param root0.playerCompositeMetricsArray - 2D array of composite metrics chunked for grid layout
 * @param root0.listPlayerSameTeamAndPosition - Query result for roster comparison data
 * @param root0.isFetchingListPlayerSameTeamAndPosition - Loading state for roster data
 * @param root0.numCols - Number of columns for metric grid layout
 * @param root0.isSmallScreen - Whether rendering on mobile screen size
 * @param root0.isDark - Whether using dark theme styling
 * @param root0.explanationModalOpen - Whether metric explanation modal is visible
 * @param root0.selectedMetricId - ID of metric selected for explanation
 * @param root0.selectedMetricValue - Value of selected metric for display
 * @param root0.selectedRosterValues - Array of roster comparison values for selected metric
 * @param root0.handleMetricSelect - Callback invoked when metric is selected for explanation
 * @param root0.handleCloseModal - Callback invoked when closing explanation modal
 * @param root0.onNavigateToReports - Callback invoked to navigate to player reports page
 * @param root0.transferValueModalOpen - Whether transfer value modal is visible
 * @param root0.handleTransferValueRangeClick - Callback invoked when transfer value metric is clicked
 * @param root0.handleCloseTransferValueModal - Callback invoked when closing transfer value modal
 * @param root0.currencyCode - Currency code for displaying monetary values
 * @param root0.playerPageHeaderEnabled - Feature flag controlling visibility of PlayerPageHeader component
 */
const PlayerDetailCenterView = ({
  player,
  isGoBack,
  isSimilarDetail,
  onAddPlayer,
  activeWatchList,
  playerCompositeMetricsArray,
  listPlayerSameTeamAndPosition,
  isFetchingListPlayerSameTeamAndPosition,
  numCols,
  isSmallScreen,
  isDark,
  explanationModalOpen,
  selectedMetricId,
  selectedMetricValue,
  selectedRosterValues,
  handleMetricSelect,
  handleCloseModal,
  onNavigateToReports,
  transferValueModalOpen,
  handleTransferValueRangeClick,
  handleCloseTransferValueModal,
  currencyCode,
  playerPageHeaderEnabled,
}: ICenterColumnProps) => (
  <Stack className="relative flex-1">
    <MetricExplanationModal
      isOpen={explanationModalOpen}
      onClose={handleCloseModal}
      metricId={selectedMetricId}
      metricValue={selectedMetricValue}
      rosterValues={selectedRosterValues}
      player={player}
      listPlayerSameTeamAndPosition={listPlayerSameTeamAndPosition}
      isFetchingListPlayerSameTeamAndPosition={
        isFetchingListPlayerSameTeamAndPosition
      }
    />
    <TransferValueModal
      isOpen={transferValueModalOpen}
      onClose={handleCloseTransferValueModal}
      player={player}
      currencyCode={currencyCode}
    />
    {playerPageHeaderEnabled && isSmallScreen ? (
      <PlayerPageHeader player={player} loading={false} isGoBack={isGoBack} />
    ) : (
      <ScrollView
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
        contentContainerClassName={getScrollContentClassName(
          playerPageHeaderEnabled,
          isSimilarDetail,
          isGoBack
        )}
      >
        {playerPageHeaderEnabled ? (
          <PlayerPageHeader
            player={player}
            loading={false}
            isGoBack={isGoBack}
          />
        ) : (
          <>
            <HeaderPlayer
              player={player}
              activeWatchList={activeWatchList}
              onAddPlayer={onAddPlayer}
              isGoBack={isGoBack}
              isSimilarDetail={isSimilarDetail}
            />
            <Stack className="gap-4 pb-4">
              <AIPlayerSummaryErrorBoundary isDark={isDark}>
                <AIPlayerSummary
                  player={player}
                  isSmallScreen={isSmallScreen}
                />
              </AIPlayerSummaryErrorBoundary>
              <Stack className={isSmallScreen ? "gap-4" : "gap-4 pt-4"}>
                <Stack
                  direction="horizontal"
                  className="flex-wrap justify-start gap-4"
                >
                  {playerCompositeMetricsArray?.flatMap(
                    playerCompositeMetrics =>
                      playerCompositeMetrics.map(
                        compositeMetric =>
                          compositeMetric && (
                            <MetricItem
                              key={compositeMetric.label}
                              compositeMetric={compositeMetric}
                              numCols={numCols}
                              isSmallScreen={isSmallScreen}
                              isDark={isDark}
                              onNavigateToReports={onNavigateToReports}
                              handleTransferValueRangeClick={
                                handleTransferValueRangeClick
                              }
                              handleMetricSelect={handleMetricSelect}
                            />
                          )
                      )
                  )}
                </Stack>
              </Stack>
            </Stack>
          </>
        )}
      </ScrollView>
    )}
    {/* Portal host container - absolutely positioned to cover content area */}
    {/* eslint-disable-next-line no-restricted-syntax -- portal-host overlay needs RN View pointerEvents="box-none" (web Box atom renders a div without it) */}
    <View style={styles.portalContainer} pointerEvents="box-none">
      {/* eslint-disable-next-line no-restricted-syntax -- portal-host fill needs RN View pointerEvents="box-none" (web Box atom renders a div without it) */}
      <View style={styles.portalHostFill} pointerEvents="box-none">
        <PortalHost name={PLAYER_DETAIL_PORTAL} />
      </View>
    </View>
  </Stack>
);

const styles = StyleSheet.create({
  portalContainer: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 100,
  },
  portalHostFill: {
    flex: 1,
  },
});

PlayerDetailCenterView.displayName = "PlayerDetailCenterView";

export default memo(PlayerDetailCenterView);
